import { SubagentAccess } from 'js/src/agent/subagents/subagentAccess';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { z } from 'zod';

import type { EnhancedToolResult, ToolExecutionContext } from '../types';

import { resolveClipSegment } from './clip-segment-resolver';
import { Tool } from './Tool';

import { resolveClipSource } from 'js/src/components/app/chat-tab/components/ask-user-question/clip-preview';
import { retrieveTranscriptForAsset } from 'js/src/data/clip-creator/clip-creator-utils';

export const proposeClipsSchema = z.object({
    media_id: z
        .string()
        .describe('Media id of the uploaded video or audio recording the clips are cut from.'),
    clips: z
        .array(
            z.object({
                title: z.string().describe('A short, specific title for the clip.'),
                reason: z
                    .string()
                    .describe(
                        'Why this works as a standalone short clip: what hooks a viewer in the first seconds and what pays off by the end.',
                    ),
                segments: z
                    .array(
                        z.object({
                            start_quote: z
                                .string()
                                .describe(
                                    'The first few words of the segment, copied exactly as the transcript spells them (filler words and repeats included). Quote enough words that they occur only once in the recording.',
                                ),
                            end_quote: z
                                .string()
                                .describe(
                                    'The last few words of the segment, copied exactly as the transcript spells them.',
                                ),
                        }),
                    )
                    .min(1)
                    .describe(
                        'The parts of the recording that make up the clip, in the order they play. Usually one. Use several only to join moments from different parts of the recording into one clip.',
                    ),
            }),
        )
        .min(1)
        .describe('The clips, best first.'),
});
export type ProposeClipsArgs = z.infer<typeof proposeClipsSchema>;

const formatTimestamp = (ms: number): string => {
    const totalSeconds = ms / 1000;
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = (totalSeconds % 60).toFixed(1).padStart(4, '0');
    return hours > 0
        ? `${hours}:${String(minutes).padStart(2, '0')}:${seconds}`
        : `${minutes}:${seconds}`;
};

type ResolvedSpan = { startMs: number; endMs: number };

const OVERLAP_NOTE_THRESHOLD = 0.5;

const spansLength = (spans: ResolvedSpan[]): number =>
    spans.reduce((total, span) => total + span.endMs - span.startMs, 0);

/** Pairs of clips sharing more than half of the shorter clip's running time. */
const findOverlappingClips = (clips: ResolvedSpan[][]): [number, number][] =>
    clips.flatMap((first, firstIndex) =>
        clips.slice(firstIndex + 1).flatMap((second, offset) => {
            const shared = first.reduce(
                (total, a) =>
                    total +
                    second.reduce(
                        (sum, b) =>
                            sum +
                            Math.max(
                                0,
                                Math.min(a.endMs, b.endMs) - Math.max(a.startMs, b.startMs),
                            ),
                        0,
                    ),
                0,
            );
            const shorter = Math.min(spansLength(first), spansLength(second));
            return shorter > 0 && shared / shorter > OVERLAP_NOTE_THRESHOLD
                ? [[firstIndex, firstIndex + offset + 1] as [number, number]]
                : [];
        }),
    );

/**
 * Proposes short clips from a long recording without building anything: each clip is located in
 * the recording's transcript by the words its segments open and close on, and comes back with
 * exact times.
 */
export class ProposeClipsTool extends Tool<typeof proposeClipsSchema> {
    protected override readonly schema = proposeClipsSchema;
    protected override readonly toolName = TOOL_NAMES.proposeClips;
    public override readonly subagentAccess = SubagentAccess.READ_ONLY;
    protected override readonly toolDescription =
        'Propose short, standalone clips cut from an uploaded recording (a podcast, interview, vlog, talk, game broadcast) — the strongest moments to post on their own. Nothing is added to the video. Each clip is one or more segments of the recording, located by quoting the transcript words each segment opens and closes on; the result gives every clip its exact times and length.';

    public override getToolDisplayName(): string {
        return 'Proposing clips';
    }

    public get statusIcon(): string {
        return 'content_cut';
    }

    public async execute(
        args: ProposeClipsArgs,
        _context: ToolExecutionContext,
    ): Promise<EnhancedToolResult> {
        const source = resolveClipSource(args.media_id);
        if (!source) {
            return {
                result: `Error: ${args.media_id} is not a playable video or audio recording. Pass the media id of the uploaded recording the clips come from.`,
                isSuccess: false,
            };
        }

        const { transcriptWords } = await retrieveTranscriptForAsset(
            source.assetUuid,
            source.isAudio,
        );
        if (transcriptWords.length === 0) {
            return {
                result: `Error: ${args.media_id} has no transcript yet. It may still be processing; wait a moment and try again.`,
                isSuccess: false,
            };
        }

        const errors: string[] = [];
        const resolvedClips = args.clips.map((clip, clipIndex) => {
            const spans = clip.segments.flatMap((segment, segmentIndex) => {
                const resolution = resolveClipSegment(transcriptWords, segment);
                if (!resolution.ok) {
                    errors.push(
                        `- "${clip.title}", segment ${segmentIndex + 1}: ${resolution.error}`,
                    );
                    return [];
                }
                return [resolution];
            });
            const durationMs = spans.reduce((total, span) => total + span.endMs - span.startMs, 0);
            const ranges = spans
                .map((span) => `${formatTimestamp(span.startMs)}–${formatTimestamp(span.endMs)}`)
                .join(' + ');
            return {
                spans,
                line: `${clipIndex + 1}. "${clip.title}" — ${ranges} (${(durationMs / 1000).toFixed(1)}s)`,
            };
        });

        if (errors.length > 0) {
            return {
                result: `Error: these segments were rejected, so no clips were proposed:\n${errors.join('\n')}\nQuote the words exactly as the transcript spells them, including filler words and repeats, and propose the clips again.`,
                isSuccess: false,
            };
        }
        const overlapNotes = findOverlappingClips(resolvedClips.map((clip) => clip.spans)).map(
            ([first, second]) =>
                `Note: clips ${first + 1} and ${second + 1} cover largely the same part of the recording. If they are the same moment, propose again without the weaker one.`,
        );
        return {
            result: [
                `Proposed ${args.clips.length} clip(s):`,
                ...resolvedClips.map((clip) => clip.line),
                ...overlapNotes,
            ].join('\n'),
            isSuccess: true,
        };
    }
}
